// OWNER: AREA-OPS (dashboard, applications/*, suppressions, audit, system). Components go in src/features/ops.
// Application detail layout: header + tabs. Each tab is its own child route file.
import { useQuery } from '@tanstack/react-query'
import { createFileRoute, Outlet } from '@tanstack/react-router'
import { applicationQuery } from '@/api/queries/applications'
import { IdText } from '@/components/CopyButton'
import { PageHeader } from '@/components/PageHeader'
import { ErrorState } from '@/components/QueryState'
import { TabLink, TabNav } from '@/components/TabNav'
import { Badge } from '@/components/ui/badge'
import { Skeleton } from '@/components/ui/skeleton'
import { LiveSendingBadge } from '@/features/ops/LiveSending'
import { ApplicationCrumb } from '@/shell/crumbs'

export const Route = createFileRoute('/applications/$appId')({
  staticData: { crumb: ApplicationCrumb },
  // Prime the cache so the header renders without a flash on navigation.
  loader: ({ context, params }) => {
    void context.queryClient.prefetchQuery(applicationQuery(params.appId))
  },
  component: ApplicationLayout,
})

const TABS = [
  { to: '/applications/$appId', label: 'Overview', exact: true },
  { to: '/applications/$appId/event-types', label: 'Event types' },
  { to: '/applications/$appId/templates', label: 'Templates' },
  { to: '/applications/$appId/senders', label: 'Senders' },
  { to: '/applications/$appId/keys', label: 'Keys' },
  { to: '/applications/$appId/webhooks', label: 'Webhooks' },
  { to: '/applications/$appId/service-bus', label: 'Service Bus' },
  { to: '/applications/$appId/variables', label: 'Variables' },
  { to: '/applications/$appId/settings', label: 'Settings' },
] as const

function ApplicationLayout() {
  const { appId } = Route.useParams()
  const app = useQuery(applicationQuery(appId))

  if (app.isError) return <ErrorState error={app.error} onRetry={() => void app.refetch()} />

  return (
    <>
      <PageHeader
        title={app.data ? app.data.name : <Skeleton className="h-7 w-56" />}
        description={app.data?.description || undefined}
        meta={
          app.data && (
            <>
              <span className="font-mono text-ink-2">{app.data.slug}</span>
              {app.data.status === 'active' ? (
                <Badge tone="success">Active</Badge>
              ) : (
                <Badge tone="warning">Suspended</Badge>
              )}
              <LiveSendingBadge app={app.data} />
              {app.data.owner_team && <span>Owned by {app.data.owner_team}</span>}
              <IdText id={app.data.id} />
            </>
          )
        }
      >
        <TabNav>
          {TABS.map((t) => (
            <TabLink
              key={t.to}
              to={t.to}
              params={{ appId }}
              activeOptions={{ exact: 'exact' in t && t.exact, includeSearch: false }}
            >
              {t.label}
            </TabLink>
          ))}
        </TabNav>
      </PageHeader>
      <Outlet />
    </>
  )
}
